import { useCallback, useEffect, useRef, useState } from 'react'
import { getNowPlayingTrackOrNull, type NowPlayingTrack } from '../../api'

const NOW_PLAYING_REFRESH_MS = 10000

type StreamNowPlayingState = {
  error: Error | null
  loading: boolean
  refresh: () => Promise<void>
  track: NowPlayingTrack | null
}

export const useStreamNowPlaying = (
  channelSlug: string | null,
  enabled: boolean
): StreamNowPlayingState => {
  const [track, setTrack] = useState<NowPlayingTrack | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const requestIdRef = useRef(0)

  const refresh = useCallback(async (): Promise<void> => {
    if (!enabled || !channelSlug) {
      requestIdRef.current += 1
      setTrack(null)
      setLoading(false)
      setError(null)
      return
    }

    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    setLoading(true)

    try {
      const nextTrack = await getNowPlayingTrackOrNull(channelSlug)
      if (requestIdRef.current !== requestId) {
        return
      }
      setTrack(nextTrack)
      setError(null)
    } catch (nextError) {
      if (requestIdRef.current !== requestId) {
        return
      }
      setError(nextError instanceof Error ? nextError : new Error('Failed to load now-playing track'))
    } finally {
      if (requestIdRef.current === requestId) {
        setLoading(false)
      }
    }
  }, [channelSlug, enabled])

  useEffect(() => {
    if (!enabled || !channelSlug) {
      requestIdRef.current += 1
      setTrack(null)
      setLoading(false)
      setError(null)
      return undefined
    }

    void refresh()
    const interval = window.setInterval(() => {
      void refresh()
    }, NOW_PLAYING_REFRESH_MS)

    return () => {
      requestIdRef.current += 1
      window.clearInterval(interval)
    }
  }, [channelSlug, enabled, refresh])

  return {
    error,
    loading,
    refresh,
    track
  }
}
